const UNLIMITED_VALIDITY_YEAR = 9000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const BUSINESS_TIME_ZONE = 'Europe/Zurich';

const formatDateParts = (date, timeZone = BUSINESS_TIME_ZONE) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;

  return year && month && day ? `${year}-${month}-${day}` : '';
};

export const getGiftValidityDateValue = (validUntil) => {
  if (!validUntil) return '';

  if (typeof validUntil === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(validUntil.trim())) {
    return validUntil.trim();
  }

  const date = new Date(validUntil);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDateParts(date);
};

export const isUnlimitedGiftValidity = (validUntil) => {
  const dateValue = getGiftValidityDateValue(validUntil);

  if (!dateValue) return false;

  const year = Number(dateValue.slice(0, 4));
  return Number.isFinite(year) && year >= UNLIMITED_VALIDITY_YEAR;
};

export const formatGiftValidity = (validUntil, language, unlimitedLabel = '') => {
  const dateValue = getGiftValidityDateValue(validUntil);

  if (!dateValue) return '';

  if (isUnlimitedGiftValidity(validUntil)) {
    return unlimitedLabel;
  }

  const [year, month, day] = dateValue.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

const toUtcDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const addUtcMonths = (date, months) => {
  const nextDate = new Date(date);
  nextDate.setUTCMonth(nextDate.getUTCMonth() + months);
  return nextDate;
};

const addUtcYears = (date, years) => {
  const nextDate = new Date(date);
  nextDate.setUTCFullYear(nextDate.getUTCFullYear() + years);
  return nextDate;
};

const getLocalizedUnit = (unit, value, labels = {}) => {
  const key = value === 1 ? unit : `${unit}s`;
  return labels[key] || key;
};

export const getGiftValidityPeriod = (validUntil, labels = {}, baseDate = new Date()) => {
  const dateValue = getGiftValidityDateValue(validUntil);

  if (!dateValue || isUnlimitedGiftValidity(validUntil)) {
    return '';
  }

  const [year, month, day] = dateValue.split('-').map(Number);
  const endDate = new Date(Date.UTC(year, month - 1, day));
  const startDate = toUtcDateOnly(baseDate);

  if (!startDate || endDate < startDate) {
    return '';
  }

  let years = endDate.getUTCFullYear() - startDate.getUTCFullYear();
  if (addUtcYears(startDate, years) > endDate) {
    years -= 1;
  }

  let remainingDate = addUtcYears(startDate, years);

  if (remainingDate > endDate) {
    years = Math.max(0, years - 1);
    remainingDate = addUtcYears(startDate, years);
  }

  let months = (endDate.getUTCFullYear() - remainingDate.getUTCFullYear()) * 12
    + endDate.getUTCMonth()
    - remainingDate.getUTCMonth();

  if (addUtcMonths(remainingDate, months) > endDate) {
    months -= 1;
  }

  months = Math.max(0, months);

  const afterMonthsDate = addUtcMonths(remainingDate, months);
  const remainingDays = Math.max(0, Math.round((endDate.getTime() - afterMonthsDate.getTime()) / MS_PER_DAY));

  if (months === 11 && remainingDays >= 25) {
    const roundedYears = years + 1;
    return `${roundedYears} ${getLocalizedUnit('year', roundedYears, labels)}`;
  }

  const parts = [];

  if (years > 0) {
    parts.push(`${years} ${getLocalizedUnit('year', years, labels)}`);
  }

  if (months > 0) {
    parts.push(`${months} ${getLocalizedUnit('month', months, labels)}`);
  }

  if (parts.length > 0) {
    return parts.join(' ');
  }

  const days = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY));

  return `${days} ${getLocalizedUnit('day', days, labels)}`;
};
